<template>
  <dynamic-object-form-structure
    :object-uuid="objectUUID"
    :object-layer="objectLayer"
    :loading-structure="loadingStructure"
    :loading-save="loadingSave"
    :permission-name="form.permission"
    :object-type-translation-key="form.name_translation"
    :tabs="visibleTabs"
    :active-tab="activeTab"
    :action-type="actionType"
    :map-element="$parent.$refs.formsContainer"
    :previous-pos-x="previousPosX"
    :previous-pos-y="previousPosY"
    :data-was-changed="dataWasChanged"
    :save-is-disabled="saveIsDisabled"
    :fields-still-need-filled="required.stillNeedFilled"
    :assets="objectData.associated_network_elements"
    :internal-reference="internalReference"
    @updateActiveTab="activeTab = $event"
    @changeViewMode="changeViewModeOfObject"
    @clearData="clearData"
    @save="saveObject"
    @close="closeForm"
    :object-data="objectData"
  >
    <template v-slot:formFields>
      <template-tab
        v-show="
          visibleTabs[activeTab].code === 'asset_reservation_template_selection'
        "
        :action-type="actionType"
        :disabled="
          actionType !== 'create' && actionType !== 'createAssetReservation'
        "
        :template-id="objectData.ar_template_id"
        @update-id="setValueInObject('ar_template_id', $event)"
        @update-template="template = $event"
        @update-only-used-elements="onlyUsedElements = $event"
      />

      <assets-tab
        v-if="visibleTabs[activeTab].code === 'asset_reservation_assets'"
        :disabled="readOnly"
        :action-type="actionType"
        :wkt="wkt"
        :template="template"
        :assets="associated_assets"
        :only-used-elements="onlyUsedElements"
        @update-assets="updateAssets($event)"
        @add-asset-to-delete="addAssetToDelete"
        style="maxHeight: 65vh"
      />

      <network-elements-tab
        v-if="
          visibleTabs[activeTab].code === 'asset_reservation_network_elements'
        "
        :disabled="readOnly"
        :action-type="actionType"
        :wkt="wkt"
        :template="template"
        :network-elements="objectData.associated_network_elements"
        @dissociate-asset-reservation-object="
          dissociateAssetReservationObject(
            $event,
            'associated_network_elements'
          )
        "
        @update-network-elements="
          objectData.associated_network_elements = $event
        "
      />

      <div
        v-show="visibleTabs[activeTab].type === 'form'"
        class="form tab-content"
      >
        <input-field
          v-for="field in visibleTabs[activeTab].fields"
          :key="visibleTabs[activeTab].code + field.label + field.order"
          ref="formInputField"
          :value="getValueInObject(field.var_name, field.input_type)"
          @input="setValueInObject(field.var_name, $event, field.input_type)"
          :label="field.label_client ? field.label_client : field.label"
          :variable-name="field.var_name"
          :inputType="field.input_type"
          :size="field.size"
          :required="
            field.var_name === 'ar_dropped_by'
              ? objectData.ar_dropped_date !== null
              : field.required
          "
          :required-filled="required.filled"
          :selectOptions="field.select_list_name"
          :radioOptions="field.radio_options"
          :fieldDisabled="
            field.var_name === 'ar_external_id'
              ? template.art_type === 'TPA'
              : field.disable
          "
          :tabDisabled="
            !userCanEditTabContent(visibleTabs[activeTab]) ||
              actionType === 'addAssetsToReservation'
          "
          :formDisabled="readOnly"
          :hide="field.hide"
          :checkboxOptions="field.checkbox_options"
          :object-data="objectData"
          :restrictSpecialCharacters="true"
          @updateCheckboxValue="updateCheckboxValue($event)"
          @removePhoto="addToPhotosToRemove($event)"
        />
      </div>
    </template>
  </dynamic-object-form-structure>
</template>

<script>
import { dynamicObjectFormsMixin } from "@/mixins/forms/dynamicObjectForms/dynamicObjectFormsMixin";
import DynamicObjectFormStructure from "../_Templates/DynamicObjectFormStructure";
import InputField from "../_Molecules/InputField";
import TemplateTab from "@/components/Home/Forms/_SpecialTabs/AssetReservationForm/TemplateTab";
import AssetsTab from "@/components/Home/Forms/_SpecialTabs/AssetReservationForm/AssetsTab";
import { mapActions } from "vuex";
import NetworkElementsTab from "@/components/Home/Forms/_SpecialTabs/AssetReservationForm/NetworkElementsTab";

export default {
  name: "AssetReservationForm",

  components: {
    NetworkElementsTab,
    AssetsTab,
    TemplateTab,
    InputField,
    DynamicObjectFormStructure
  },

  mixins: [dynamicObjectFormsMixin],

  data() {
    return {
      /**
       * Chosen template by the user
       */
      template: null,

      /**
       * Indicates if the save button is disabled
       */
      saveIsDisabled: true,

      /**
       * Indicates if only used assets should be listed
       */
      onlyUsedElements: null,
      internalReference: null,
      itemsToBeDeleted: [],
      associated_assets: []
    };
  },

  computed: {
    /**
     * Indicates if the action is create or createAssetReservation
     * @returns {Boolean} indicating if the actionType fits the condition
     */
    actionTypeIsCreateOrCreateAssetReservation() {
      return (
        this.actionType === "create" ||
        this.actionType === "createAssetReservation"
      );
    }
  },

  /**
   * If the actionType is edit the save button is enabled
   */
  mounted() {
    if (this.actionType === "edit" || this.actionType === "addNetworkElementsToReservation"){
      this.saveIsDisabled = false;
    }
    if (this.actionType === "createAssetReservation") {
      this.objectData.ar_created_at = null;
      this.objectData.ar_updated_at = null;
    }
  },

  watch: {
    /**
     * Watches for chnges in the value of loadingStructure
     * If there isn't a new value and the actionType is create or createAssetReservation
     * the associated assets and network elements are cleared
     */
    loadingStructure(newValue) {
      if (!newValue && this.actionTypeIsCreateOrCreateAssetReservation) {
        this.objectData.associated_assets = [];
        this.objectData.associated_network_elements = [];
        this.associated_assets = [];
      }
    },

    /**
     * Watches for changes in the value of the template
     * If the actionType is create or createAssetReservation the asset reservation data is reset
     * and the tempate default values are applied
     */
    template() {
      if (this.actionTypeIsCreateOrCreateAssetReservation) {
        this.resetAssetReservationData();
        this.applyTemplateDefaultValues();
        this.saveIsDisabled = !(
          this.associated_assets &&
          this.associated_assets.length > 0 &&
          !!this.objectData.ar_internal_id
        );
      }
    },

    /**
     * Watcher for the dropped date.
     * If the new value is not null the field droppedBy is made required.
     * Else, the field is removed from the required fields.
     *
     * @param {String} newValue - the new value for ar_dropped_date
     */
    "objectData.ar_dropped_date"(newValue) {
      let droppedByField = this.form.tabs[3].fields.find(
        item => item.var_name === "ar_dropped_by"
      );
      if (newValue !== null) {
        droppedByField.required = true;
        this.registerRequiredField(droppedByField);
      } else {
        let index = this.required.fields.findIndex(
          field => field.var_name === "ar_dropped_by"
        );
        if (index > -1) {
          this.required.fields.splice(index, 1);
        }

        droppedByField.required = false;
      }
      this.checkRequiredFieldsAreFilled();
    },

    internalReference() {
      this.saveIsDisabled = !(
        this.associated_assets &&
        this.associated_assets.length > 0 &&
        !!this.objectData.ar_internal_id
      );
    },

    "objectData.ar_uuid"(newValue) {
      this.associated_assets = this.objectData.associated_assets;
    }
  },

  methods: {
    ...mapActions([
      "createAssetReservation",
      "editAssetReservation",
      "createAssetReservationObject",
      "deleteAssetReservationObject",
      "deleteObjectByUUID"
    ]),

    /**
     * Checks if the tab is enabled
     * @param {Object} tab - the tab to be checked
     * @returns {Boolean} indicating if the tab is enabled or not
     */
    tabIsEnabled(tab) {
      if (tab.code.includes("assets")) {
        return !!this.template;
      }
      return !(
        tab.code.includes("network_elements") &&
        this.actionTypeIsCreateOrCreateAssetReservation
      );
    },

    /**
     * Saves the asset reservation, according to the actionType
     */
    async saveObject() {
      this.checkRequiredFieldsAreFilled();
      if (!this.required.filled) {
        return;
      }
      let objectsToAssociate;
      this.loadingSave = true;
      //dissociate Asset Reservation Objects
      if(this.itemsToBeDeleted.length > 0){
        for(let i=0; i < this.itemsToBeDeleted.length; i++){
          await this.dissociateAssetReservationObject(this.itemsToBeDeleted[i],'associated_assets');
        }
      }
      switch (this.actionType) {
        case "create":
        case "createAssetReservation":
          this.createObject();
          break;
        case "edit":
          if (Object.keys(this.dataToSend).length > 1) {
            this.editObject();
          } else {
            this.closeForm();
          }
          break;
        case "addAssetsToReservation":
        case "addNetworkElementsToReservation":
          objectsToAssociate = this.getListOfAssetReservationObjectsToAssociate();
          if (objectsToAssociate.length > 0) {
            this.changeAssociatedAssets(objectsToAssociate);
          } else {
            this.closeForm();
          }
          break;
        default:
          this.closeForm();
      }
    },

    addAssetToDelete(object){
      const newItemsArray = [];
      //create new array without deleted item
      this.associated_assets.forEach(item =>{
        if(item.object_id !== object.object_id){
          newItemsArray.push(item);
        }
      });
      this.associated_assets = [...newItemsArray];
      this.itemsToBeDeleted.push(object);
      this.dataWasChanged = true;
    },
    /**
     * Creates the asset reservation
     */
    async createObject() {
      this.placeObjectDataInDataToSend();
      let assetReservationID = this.objectData.ar_id;
      const associatedAssets = [];
      this.associated_assets.forEach(item =>{
        associatedAssets.push({
          object_layer: item.aro_object_layer,
          object_uuid: item.aro_object_uuid
        })
      });
      this.objectData.associated_assets = associatedAssets;
      const dataToSendCopy = { ...this.dataToSend, objects: associatedAssets };
      if (this.actionType === "createAssetReservation") {
        delete dataToSendCopy.created_at;
        delete dataToSendCopy.updated_at;
        delete dataToSendCopy.deleted_at;
      }
      try {

        assetReservationID = (await this.createAssetReservation(dataToSendCopy))[0].ar_id;
        // await this.createAssetReservation(dataToSendCopy);
        this.finishObjectCreation();
      } catch (error) {
        let key = Object.keys(error.data.message.errors)[0];
        let value = error.data.message.errors[key][0];
        let valueIsRequired = value.split(".")[1] === "required" ? true : false;
        if (valueIsRequired) {
          this.$toasted.global.toast_error(
            "The field " + key + " is required."
          );
        } else {
          this.$toasted.global.toast_error("An error occurred");
        }
        this.loadingSave = false;
      } finally {
        this.checkTPASync(assetReservationID);
      }
    },

    /**
     * Edits the asset reservation
     */
    async editObject() {
      let assetReservationUUID = this.objectData.ar_uuid;
      let assetReservationID = this.objectData.ar_id;

      try {
        await this.editAssetReservation({
          uuid: assetReservationUUID,
          data: this.dataToSend
        });
        this.updateGeoServer();
        this.closeForm();
        this.$toasted.global.toast_success(this.$i18n.t("EditionSuccess"));
      } catch (error) {
        console.error(error);
        this.$toasted.global.toast_error(error.data.data);
      } finally {
        this.checkTPASync(assetReservationID);
        this.loadingSave = false;
      }
    },

    /**
     * Updates the asset reservation objects
     * @param {Array} objectsToAssociate - the assets to be associated to the asset reservation
     */
    async changeAssociatedAssets(objectsToAssociate) {
      let assetReservationUUID = this.objectData.ar_uuid;
      let assetReservationID = this.objectData.ar_id;

      try {
        await this.createNewAssetReservationObjects(
          assetReservationUUID,
          objectsToAssociate
        );
        this.updateGeoServer();
        this.closeForm();
        this.$toasted.global.toast_success(this.$i18n.t("EditionSuccess"));
      } catch (error) {
        this.$toasted.global.toast_error(error.data.data);
      } finally {
        this.checkTPASync(assetReservationID);
        this.loadingSave = false;
      }
    },

    /**
     * Return a list of assets to be associated
     * @returns {Array} the list of assets
     */
    getListOfAssetReservationObjectsToAssociate() {
      let assetsToAssociate = this.associated_assets.filter(
        object => !object.hasOwnProperty("aro_id") && !object.delete
      );
      let networkElementsToAssociate = this.objectData.associated_network_elements.filter(
        object => !object.hasOwnProperty("aro_id") && !object.delete
      );
      return assetsToAssociate.concat(networkElementsToAssociate);
    },

    /**
     * Creates the assets of the asset reservation
     */
    async createNewAssetReservationObjects(
      assetReservationUUID,
      newAssetReservationObjects
    ) {
      for (const assetReservationObject of newAssetReservationObjects) {
        let objectLayer = assetReservationObject.aro_object_layer;
        let objectUUID = assetReservationObject.aro_object_uuid;

        try {
          await this.createAssetReservationObject({
            assetReservationUUID: assetReservationUUID,
            assetReservationObject: {
              object_layer: objectLayer,
              object_uuid: objectUUID
            }
          });
        } catch (error) {
          this.emitAssetReservationObjectAssociationErrorMessage(
            objectLayer,
            assetReservationObject.asset,
            this.$i18n.t("ErrorAssociationAssetReservationWithObject")
          );
        }
      }
    },

    /**
     * Emits an error message indicating that the association of the assets failed
     */
    emitAssetReservationObjectAssociationErrorMessage(
      objectLayer,
      asset,
      message
    ) {
      let layerName = this.$store.getters.getLayerTranslationKey(objectLayer);
      let layerPrefix = this.$store.getters.getLayerInitials(objectLayer);

      this.$toasted.global.toast_error(
        message + this.$i18n.tc(layerName, 1) + " " + asset[layerPrefix + "_id"]
      );
    },

    /**
     *
     * @param {Object} objectToDelete - the asset to be dissociated
     * @param {String} type - the type of the asset
     */
    async dissociateAssetReservationObject(objectToDelete, type) {
      try {
        if (objectToDelete.aro_id) {
          await this.deleteAssetReservationObject({
            assetReservationUUID: this.objectData.ar_uuid,
            objectUUID: objectToDelete.aro_object_uuid
          });
        }
        this.objectData[type] = this.objectData[type].filter(
          object => object.aro_id !== objectToDelete.aro_id
        );
        this.saveIsDisabled = !(
          this.associated_assets &&
          this.associated_assets.length > 0 &&
          !!this.objectData.ar_internal_id
        );
        this.updateGeoServer();
      } catch (e) {
        console.log(e);
      }
    },

    /**
     * Applies the selected template default values
     */
    applyTemplateDefaultValues() {
      if (this.template && this.template.request_fields) {
        this.template.request_fields.forEach(field => {
          if (field !== "ar_created_at" && field !== "ar_updated_at") {
            this.setValueInObject(
              field.request_form_field.var_name,
              field.artf_default_value
            );
          }
        });
      }
    },

    /**
     * Resets the asset reservation data
     */
    resetAssetReservationData() {
      for (let field in this.objectData) {
        if (field !== "ar_template_id") {
          if (
            field === "associated_assets" ||
            field === "associated_network_elements"
          ) {
            this.setValueInObject(field, []);
          } else if (field !== "ar_created_at" && field !== "ar_updated_at") {
            this.setValueInObject(field, null);
          }
        }
      }
    },

    /**
     * Updates the asset reservation associated assets
     * @param {Array} assets - the updated assets
     */
    updateAssets(assets) {
      if (this.associated_assets.length > assets.length) {
        let assetsToDelete = this.associated_assets.filter(previousAsset => {
          return !assets.some(asset => { return asset.object_id === previousAsset.object_id })
        })
        if (this.actionType === 'edit' || this.actionType === 'addAssetsToReservation') {
          this.itemsToBeDeleted = [...assetsToDelete];
        }
      }

      this.saveIsDisabled = !(
        this.associated_assets &&
        this.associated_assets.length > 0 &&
        !!this.objectData.ar_internal_id
      );
      this.associated_assets = assets;
    },

    checkTPASync(ar_id) {
      if (this.template.art_type === "TPA") {
        this.$emit("open-sync-dialog", ar_id);
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/Home/Forms/ObjectForms/AssetReservationForm.vue